// Motif placeholders: the drawing a card shows when its piece has no image.
//
// Each card gets one of fourteen motifs (circles, a ruler, a staircase, a dot
// matrix…) drawn in the site's line (--line-width, 2px), round caps, ink on the
// stage tint, with ink used only as an accent. The motif and every detail in
// it come from the seed, so a card always shows the same drawing and nothing
// is drawn by hand. Explored in public/_mock/cards.html.
//
// Theme-aware: lines and accents are currentColor (set the container's color
// to --foreground), page-colour fills are var(--background), so the drawing
// follows light and dark. The container needs container-type: inline-size,
// because the type motifs are sized in cqi.

function hash(s: string): number {
  let h = 2166136261;
  for (const c of s) {
    h ^= c.charCodeAt(0);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

type Rng = (() => number) & { int: (a: number, b: number) => number };

function makeRng(seed: number): Rng {
  let x = seed || 1;
  const f = (() => {
    x ^= x << 13;
    x ^= x >>> 17;
    x ^= x << 5;
    return ((x >>> 0) % 100000) / 100000;
  }) as Rng;
  f.int = (a, b) => a + Math.floor(f() * (b - a + 1));
  return f;
}

const r1 = (n: number) => Math.round(n * 10) / 10;
const INK = 'currentColor';
const PAGE = 'style="fill:var(--background)"';
const FILL = `fill="${INK}"`;
// The site's one line weight, and the two emphasised strokes, which were 2.5
// and 3 against the old 1.5 line, so they keep that proportion to it.
const LINE = 'var(--line-width)';
const LINE_STRONG = 'calc(var(--line-width) * 5 / 3)';
const LINE_STRONGER = 'calc(var(--line-width) * 2)';

// SVG on a 100 × 80 canvas, the card's 5:4 frame. Strokes are --line-width at
// any card size (non-scaling-stroke), so they match the card's frame; the
// emphasised strokes are LINE_STRONG and LINE_STRONGER.
//
// Composition rules, so no drawing looks like an accident:
// - Contained drawings keep M on every side, and are centred on their real
//   extents, so the space above matches the space below.
// - Offset drawings are anchored to an edge or corner and cross it decisively.
//   Nothing comes within NEAR of an edge without crossing it: a near miss reads
//   as a mistake.
// - Accents sit exactly where they mean something: the overlap, the end point.
const M = 12;
const NEAR = 6;
// Contained drawings fit the frame (meet), so their margins survive a frame
// wider than 5:4, like the featured card. Drawings that bleed fill it (slice),
// so they still run off the real edge.
function svg(body: string, bleed = false): string {
  const b = body.replace(/<(path|line|circle|rect|polyline)\b/g, '<$1 vector-effect="non-scaling-stroke"');
  return `<svg viewBox="0 0 100 80" width="100%" height="100%" preserveAspectRatio="xMidYMid ${bleed ? 'slice' : 'meet'}" style="position:absolute;inset:0;stroke-width:${LINE}" fill="none" stroke="${INK}" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${b}</svg>`;
}

// Words a title's first letter shouldn't come from: "The Ramstack" draws an R.
const SKIP = new Set(['the', 'a', 'an', 'on', 'of', 'in', 'is', 'my', 'and', 'to']);

type Params = { centred?: boolean; corner?: boolean; n?: number; ceiling?: boolean; ch?: string };

const MOTIFS: Record<string, (r: Rng, title: string, p: Params) => string> = {
  letter(_r, title, p) {
    if (p.ch) return `<div aria-hidden="true" style="position:absolute;right:-4cqi;bottom:-38cqi;font-size:110cqi;line-height:1;color:transparent;-webkit-text-stroke:${LINE} ${INK}">${p.ch}</div>`;
    const word = title.split(/\s+/).find((w) => !SKIP.has(w.toLowerCase().replace(/[^a-z]/g, ''))) || title;
    const ch = (word.match(/[A-Za-z]/) || ['A'])[0].toUpperCase();
    return `<div aria-hidden="true" style="position:absolute;right:-6cqi;bottom:-26cqi;font-size:92cqi;line-height:1;letter-spacing:-.05em;color:transparent;-webkit-text-stroke:${LINE} ${INK}">${ch}</div>`;
  },
  venn(r, _t, p) {
    const n = p.n ?? r.int(2, 3);
    const rad = r1(15 + r() * 4);
    if (n === 2) {
      const d = rad * 0.55;
      return svg(`<circle cx="${r1(50 - d)}" cy="40" r="${rad}"/><circle cx="${r1(50 + d)}" cy="40" r="${rad}"/><circle cx="50" cy="40" r="1.6" ${FILL}/>`);
    }
    // three centres on an equilateral triangle around the centroid; the group's
    // box runs from -d - rad to d/2 + rad, so shift by d/4 to centre it
    const d = rad * 0.6;
    const cy = 40 + d / 4;
    const pts = [[50, cy - d], [50 + d * 0.866, cy + d / 2], [50 - d * 0.866, cy + d / 2]];
    return svg(pts.map(([x, y]) => `<circle cx="${r1(x)}" cy="${r1(y)}" r="${rad}"/>`).join('') + `<circle cx="50" cy="${r1(cy)}" r="1.6" ${FILL}/>`);
  },
  ruler(r, _t, p) {
    const n = r.int(14, 22);
    const k = r.int(3, n - 3);
    const step = (100 - 2 * M) / (n - 1);
    // ticks run from y 27 to 53, centred on the canvas's 40
    let b = `<line x1="${M}" y1="53" x2="${100 - M}" y2="53"/>`;
    for (let i = 0; i < n; i++) {
      const x = r1(M + i * step);
      const h = i === k ? 26 : i % 5 === 0 ? 9 : 5;
      b += `<line x1="${x}" y1="53" x2="${x}" y2="${53 - h}"${i === k ? ` style="stroke-width:${LINE_STRONG}"` : ''}/>`;
    }
    // the ceiling: a line the tallest tick runs into and can't pass
    if (p.ceiling) return svg(b + `<line x1="${M}" y1="27" x2="${100 - M}" y2="27"/>`);
    return svg(b + `<circle cx="${r1(M + k * step)}" cy="27" r="2" ${FILL}/>`);
  },
  grid(r) {
    const c = r.int(5, 8);
    const rows = Math.round(c * 0.62);
    const s = r1(Math.min((100 - 2 * M) / c, (80 - 2 * M) / rows));
    const ox = r1((100 - s * c) / 2);
    const oy = r1((80 - s * rows) / 2);
    const k = r.int(0, c * rows - 1);
    let b = '';
    for (let j = 0; j < rows; j++)
      for (let i = 0; i < c; i++)
        b += `<rect x="${r1(ox + i * s + 1)}" y="${r1(oy + j * s + 1)}" width="${r1(s - 2)}" height="${r1(s - 2)}" rx="1"${j * c + i === k ? ` ${FILL}` : ''}/>`;
    return svg(b);
  },
  prose(r) {
    const n = r.int(7, 10);
    let b = '';
    for (let i = 0; i < n; i++) {
      const y = r1(M + i * ((80 - 2 * M) / (n - 1)));
      const w = i === n - 1 ? 20 + r() * 25 : 50 + r() * (100 - 2 * M - 50);
      b += `<line x1="${M}" y1="${y}" x2="${r1(M + w)}" y2="${y}"${i === 0 ? ` style="stroke-width:${LINE_STRONGER}"` : ''}/>`;
    }
    return svg(b);
  },
  orbit(r, _t, p) {
    const centred = p.centred ?? r() < 0.5;
    // centred: rings stay inside the margin. Offset: the centre sits on a side
    // edge and the rings cross the frame boldly.
    const cx = centred ? 50 : r() < 0.5 ? M : 100 - M;
    const step = centred ? 7 : 13;
    const n = centred ? r.int(3, 4) : 5;
    const rings: number[] = [];
    for (let i = 1; i <= n; i++) {
      const rad = i * step;
      // drop any ring that would nearly touch an edge without crossing it
      const gaps = [cx - rad, 100 - cx - rad, 40 - rad];
      if (gaps.some((g) => g > 0 && g < NEAR) || gaps.some((g) => g < 0 && g > -NEAR)) continue;
      rings.push(rad);
    }
    let b = rings.map((rad) => `<circle cx="${cx}" cy="40" r="${rad}"/>`).join('');
    // the body sits on a ring that's fully in view, on the side facing the canvas
    const visible = rings.filter((rad) => rad <= Math.min(40, 100 - cx, cx) - 4);
    const ring = visible.length ? visible[r.int(0, visible.length - 1)] : rings[0];
    const toward = cx < 50 ? 0 : Math.PI;
    const a = toward + (r() - 0.5) * (Math.PI / 1.5);
    b += `<circle cx="${cx}" cy="40" r="2" ${FILL}/><circle cx="${r1(cx + Math.cos(a) * ring)}" cy="${r1(40 + Math.sin(a) * ring)}" r="3.2" ${PAGE}/>`;
    return svg(b, !centred);
  },
  steps(r) {
    const n = r.int(4, 6);
    const w = (100 - 2 * M) / n;
    const h = (80 - 2 * M) / n;
    let d = `M${M} ${80 - M}`;
    for (let i = 0; i < n; i++) d += ` V${r1(80 - M - (i + 1) * h)} H${r1(M + (i + 1) * w)}`;
    return svg(`<path d="${d}"/><circle cx="${100 - M}" cy="${M}" r="2" ${FILL}/>`);
  },
  line(r) {
    const n = r.int(6, 9);
    const pts: number[][] = [];
    for (let i = 0; i < n; i++) {
      const damp = 1 - i / (n - 1);
      pts.push([r1(M + i * ((100 - 2 * M) / (n - 1))), r1(40 + (r() - 0.5) * 44 * damp)]);
    }
    const end = pts[n - 1];
    return svg(`<polyline points="${pts.map((p) => p.join(',')).join(' ')}"/><circle cx="${end[0]}" cy="${end[1]}" r="2.2" ${FILL}/>`);
  },
  nested(r, _t, p) {
    const n = r.int(3, 5);
    const corner = p.corner ?? r() < 0.5; // share the top-left corner, or sit concentric
    let b = '';
    for (let i = 0; i < n; i++) {
      const inset = i * 7;
      const w = 100 - 2 * M - (corner ? inset : 2 * inset);
      const h = 80 - 2 * M - (corner ? inset * 0.8 : 2 * inset * 0.8);
      const x = corner ? M : M + inset;
      const y = corner ? M : M + inset * 0.8;
      // corners stay concentric: each radius is the outer one minus the inset
      b += `<rect x="${r1(x)}" y="${r1(y)}" width="${r1(w)}" height="${r1(h)}" rx="${Math.max(1, 8 - i * 2)}"/>`;
    }
    return svg(b);
  },
  punch(r) {
    const c = 14;
    const rows = 8;
    const s = (100 - 2 * M) / (c - 1);
    const oy = (80 - (rows - 1) * s * 0.85) / 2; // centred vertically
    let b = '';
    for (let j = 0; j < rows; j++)
      for (let i = 0; i < c; i++) {
        if (r() < 0.35) continue;
        b += `<circle cx="${r1(M + i * s)}" cy="${r1(oy + j * s * 0.85)}" r="1.4"${r() < 0.15 ? ` ${FILL}` : ''}/>`;
      }
    return svg(b);
  },
  arcs(r) {
    const s = 12.6;
    const ox = (100 - 6 * s) / 2;
    const oy = (80 - 4 * s) / 2;
    let b = '';
    for (let j = 0; j < 4; j++)
      for (let i = 0; i < 6; i++) {
        const x = r1(ox + i * s);
        const y = r1(oy + j * s);
        const h = r1(s / 2);
        b += r() < 0.5
          ? `<path d="M${r1(x + h)} ${y} A${h} ${h} 0 0 1 ${x} ${r1(y + h)} M${r1(x + s)} ${r1(y + h)} A${h} ${h} 0 0 0 ${r1(x + h)} ${r1(y + s)}"/>`
          : `<path d="M${r1(x + h)} ${y} A${h} ${h} 0 0 0 ${r1(x + s)} ${r1(y + h)} M${x} ${r1(y + h)} A${h} ${h} 0 0 1 ${r1(x + h)} ${r1(y + s)}"/>`;
      }
    return svg(b);
  },
  page(r) {
    const bars = Array.from({ length: 5 }, (_, i) =>
      `<div style="height:2cqi;border-radius:1cqi;margin-top:${i ? 2 : 0}cqi;width:${r1(35 + r() * 55)}%;background:${INK};opacity:${i ? 0.16 : 1}"></div>`
    ).join('');
    return `<div aria-hidden="true" style="position:absolute;left:${r1(22 + r() * 14)}cqi;top:14cqi;width:80cqi;height:80cqi;padding:5cqi;background:var(--background);border:${LINE} solid ${INK};border-radius:1.4cqi">${bars}</div>`;
  },
  fan() {
    let b = '<rect x="26" y="10" width="48" height="16" rx="1.5"/><line x1="50" y1="29" x2="50" y2="35"/><line x1="21" y1="35" x2="79" y2="35"/>';
    for (const x of [21, 50, 79])
      b += `<line x1="${x}" y1="35" x2="${x}" y2="45"/><polyline points="${x - 2.4},42.6 ${x},45 ${x + 2.4},42.6"/><rect x="${x - 12}" y="48" width="24" height="22" rx="1.5"/>`;
    return svg(b);
  },
  horizon(r) {
    // a sun half set: the line, and only the part of the circle above it
    const rad = r1(11 + r() * 6);
    const y = r1(40 + rad / 2); // the drawing spans y - rad to y: centred on 40
    const cx = 50; // centred on the horizon
    return svg(`<line x1="${M}" y1="${y}" x2="${100 - M}" y2="${y}"/><path d="M${r1(cx - rad)} ${y} A${rad} ${rad} 0 0 1 ${r1(cx + rad)} ${y}"/>`);
  },
  // dots travel off the edge; a hollow one stays behind
  departs() {
    let b = `<circle cx="22" cy="40" r="6"/><line x1="34" y1="40" x2="100" y2="40"/>`;
    for (const x of [50, 64, 78, 92, 104]) b += `<circle cx="${x}" cy="40" r="2.4" ${FILL}/>`;
    return svg(b, true);
  },
  // a path forks: one branch runs off the frame, the other stops short
  fork() {
    return svg(`<path d="M${M} 40 H38 C52 40 54 22 68 22 H100"/><path d="M38 40 C52 40 54 58 68 58"/><circle cx="68" cy="58" r="2.6" ${PAGE}/>`, true);
  },
  // the same content in two shapes of equal area
  shapes() {
    // square → arrow → circle at equal gaps, the group centred. The circle
    // has the square's area (r = side / √π), so only the shape changes.
    const side = 24, rad = r1(side / Math.sqrt(Math.PI)), arrow = 8, gap = 7;
    const x0 = r1((100 - (side + gap + arrow + gap + rad * 2)) / 2);
    const ax = x0 + side + gap, cx = r1(ax + arrow + gap + rad);
    return svg(`<rect x="${x0}" y="${40 - side / 2}" width="${side}" height="${side}" rx="1.5"/><circle cx="${r1(x0 + side / 2)}" cy="40" r="1.8" ${FILL}/>` +
      `<line x1="${r1(ax)}" y1="40" x2="${r1(ax + arrow)}" y2="40"/><polyline points="${r1(ax + arrow - 2.4)},37.6 ${r1(ax + arrow)},40 ${r1(ax + arrow - 2.4)},42.4"/>` +
      `<circle cx="${cx}" cy="40" r="${rad}"/><circle cx="${cx}" cy="40" r="1.8" ${FILL}/>`);
  },
  // a line, and a small flag planted on it
  flag() {
    return svg(`<line x1="${M}" y1="52" x2="${100 - M}" y2="52"/><line x1="58" y1="52" x2="58" y2="26"/><path d="M58 26 L71 30.5 L58 35 Z" ${FILL}/>`);
  },
  // a spiral that comes back around, ending where it started reading
  spiral() {
    const pts: string[] = [];
    for (let t = 0; t <= Math.PI * 5; t += 0.12) {
      const rad = 2 + t * 1.65;
      pts.push(`${r1(50 + Math.cos(t) * rad)},${r1(40 + Math.sin(t) * rad)}`);
    }
    const [ex, ey] = pts[pts.length - 1].split(',');
    return svg(`<polyline points="${pts.join(' ')}"/><circle cx="${ex}" cy="${ey}" r="2" ${FILL}/>`);
  },
  // layers stacked front to back
  layers() {
    let b = '';
    for (let i = 3; i >= 0; i--) b += `<rect x="${18 + i * 6}" y="${15 + i * 11}" width="52" height="14" rx="1.5" ${PAGE}/>`;
    return svg(b);
  },
  // two points and the distance between them, dimensioned
  measure() {
    return svg(`<circle cx="22" cy="48" r="5" ${FILL}/><circle cx="78" cy="48" r="5"/>` +
      `<line x1="22" y1="30" x2="78" y2="30"/><line x1="22" y1="25" x2="22" y2="35"/><line x1="78" y1="25" x2="78" y2="35"/>` +
      `<line x1="22" y1="36" x2="22" y2="41" stroke-dasharray="1 2.5"/><line x1="78" y1="36" x2="78" y2="41" stroke-dasharray="1 2.5"/>`);
  },
  // a hand's line and a machine's, running together
  parallel() {
    const pts: string[] = [];
    for (let x = M; x <= 100 - M; x += 2) pts.push(`${x},${r1(48 + Math.sin(x / 5) * 3 + Math.sin(x / 2.3) * 1.2)}`);
    return svg(`<line x1="${M}" y1="32" x2="${100 - M}" y2="32"/><polyline points="${pts.join(' ')}"/>`);
  },
  // two rails and a line bouncing between them
  rails() {
    const pts: string[] = [];
    for (let i = 0; i <= 8; i++) pts.push(`${r1(M + i * ((100 - 2 * M) / 8))},${i % 2 ? 26 : 54}`);
    return svg(`<line x1="0" y1="24" x2="100" y2="24"/><line x1="0" y1="56" x2="100" y2="56"/><polyline points="${pts.join(' ')}"/>`, true);
  },
  // a shape and its reflection
  mirror() {
    return svg(`<line x1="50" y1="${M}" x2="50" y2="${80 - M}" stroke-dasharray="1 3"/><circle cx="32" cy="40" r="11" ${FILL}/><circle cx="68" cy="40" r="11"/>`);
  },
  // a point with lines radiating out of it
  burst(r) {
    let b = `<circle cx="50" cy="40" r="3" ${FILL}/>`;
    for (let i = 0; i < 16; i++) {
      const a = (i / 16) * Math.PI * 2;
      const len = 16 + r() * 10;
      b += `<line x1="${r1(50 + Math.cos(a) * 9)}" y1="${r1(40 + Math.sin(a) * 9)}" x2="${r1(50 + Math.cos(a) * len)}" y2="${r1(40 + Math.sin(a) * len)}"/>`;
    }
    return svg(b);
  },
  // an outline drawn bigger than the shape standing in it
  dashed() {
    return svg(`<rect x="${M + 8}" y="${M}" width="${100 - 2 * M - 16}" height="${80 - 2 * M}" rx="6" stroke-dasharray="3 3"/><circle cx="50" cy="${r1(80 - M - 12)}" r="9"/>`);
  },
  // two things working against each other: discs turning opposite ways,
  // pressed together, with shine thrown off where they meet
  rotor() {
    const R = 16, y = 40, lx = 50 - R, rx = 50 + R;
    let b = `<circle cx="${lx}" cy="${y}" r="${R}"/><circle cx="${rx}" cy="${y}" r="${R}"/>`;
    // one arc inside each disc, its head showing the turn: left clockwise,
    // right counter-clockwise, so their surfaces drag at the contact
    const arc = (cx: number, cw: boolean) => {
      const rr = 9, a0 = -2.2, a1 = 0.6;
      const [s, e] = cw ? [a0, a1] : [Math.PI - a0, Math.PI - a1];
      const sx = r1(cx + Math.cos(s) * rr), sy = r1(y + Math.sin(s) * rr);
      const ex = r1(cx + Math.cos(e) * rr), ey = r1(y + Math.sin(e) * rr);
      const tangent = cw ? e + Math.PI / 2 : e - Math.PI / 2;
      const h1 = tangent + 2.5, h2 = tangent - 2.5;
      return `<path d="M${sx} ${sy} A${rr} ${rr} 0 0 ${cw ? 1 : 0} ${ex} ${ey}"/>` +
        `<polyline points="${r1(ex + Math.cos(h1) * 2.6)},${r1(ey + Math.sin(h1) * 2.6)} ${ex},${ey} ${r1(ex + Math.cos(h2) * 2.6)},${r1(ey + Math.sin(h2) * 2.6)}"/>`;
    };
    b += arc(lx, true) + arc(rx, false);
    // shine at the contact, above and below
    for (const s of [-1, 1])
      for (const [dx, from, to] of [[0, 4, 9], [-3.2, 4.4, 7.6], [3.2, 4.4, 7.6]])
        b += `<line x1="${r1(50 + dx * (from / 9))}" y1="${r1(y + s * from)}" x2="${r1(50 + dx * (to / 7.6))}" y2="${r1(y + s * to)}"/>`;
    return svg(b);
  },
  // a tangle on the left that works itself out into one straight line:
  // messy to clear, read left to right
  untangle(r) {
    // one continuous line of loops: large and irregular at the start, each
    // smaller and calmer than the last, until they flatten into a straight run
    const pts: string[] = [];
    const turns = 6;
    const steps = 260;
    for (let i = 0; i <= steps; i++) {
      const u = i / steps;
      const theta = u * turns * Math.PI * 2;
      const decay = Math.pow(1 - Math.min(1, u / 0.82), 1.6);
      const wobble = 1 + 0.35 * Math.sin(theta * 0.37 + r() * 0.15);
      const rad = 15 * decay * wobble;
      // starts one loop-width in, so the biggest loop clears the margin
      const x = M + 14 + u * 56 + Math.cos(theta) * rad * 0.9;
      const y = 40 - Math.sin(theta) * rad;
      pts.push(`${r1(x)},${r1(y)}`);
    }
    pts.push(`${100 - M - 3},40`);
    return svg(`<polyline points="${pts.join(' ')}"/><circle cx="${100 - M}" cy="40" r="2.4" ${FILL}/>`);
  },
  // triage: a scatter of ideas falls into a funnel, and one comes out
  funnel(r) {
    // ideas: hollow circles of mixed sizes, spaced so none touch
    const dots: number[][] = [];
    for (let tries = 0; dots.length < 22 && tries < 600; tries++) {
      const d = [18 + r() * 64, M + 2 + r() * 16, 0.9 + r() * 1.8];
      if (dots.every(([x, y, s]) => Math.hypot(x - d[0], y - d[1]) > s + d[2] + 2.2)) dots.push(d);
    }
    let b = dots.map(([x, y, s]) => `<circle cx="${r1(x)}" cy="${r1(y)}" r="${r1(s)}"/>`).join('');
    // the funnel: two curves narrowing to a short neck
    b += `<path d="M20 35 C34 38 46 46 47.5 56 V61"/><path d="M80 35 C66 38 54 46 52.5 56 V61"/>`;
    // the one that made it
    b += `<circle cx="50" cy="${80 - M - 2.5}" r="2.5" ${FILL}/>`;
    return svg(b);
  },
  // a grid of different ideas, one of them chosen
  choices(r) {
    const cols = 5, rows = 3, s = 14, z = 4.2; // cell, and half a shape
    const ox = (100 - cols * s) / 2 + s / 2, oy = (80 - rows * s) / 2 + s / 2;
    const k = r.int(0, cols * rows - 1);
    const kinds = ['circle', 'square', 'triangle', 'diamond', 'cross', 'ring', 'bar', 'half'];
    const grid: string[] = [];
    let b = '';
    for (let j = 0; j < rows; j++)
      for (let i = 0; i < cols; i++) {
        const x = r1(ox + i * s), y = r1(oy + j * s), n = j * cols + i;
        // a mix, seeded: never the same shape as the one beside or above
        let kind = kinds[r.int(0, kinds.length - 1)];
        while (kind === grid[n - 1] && i > 0 || kind === grid[n - cols]) kind = kinds[r.int(0, kinds.length - 1)];
        grid[n] = kind;
        const f = n === k ? ` ${FILL}` : '';
        if (kind === 'circle') b += `<circle cx="${x}" cy="${y}" r="${z}"${f}/>`;
        if (kind === 'square') b += `<rect x="${r1(x - z)}" y="${r1(y - z)}" width="${2 * z}" height="${2 * z}" rx="1"${f}/>`;
        if (kind === 'triangle') b += `<path d="M${x} ${r1(y - z)} L${r1(x + z)} ${r1(y + z * 0.8)} L${r1(x - z)} ${r1(y + z * 0.8)} Z"${f}/>`;
        if (kind === 'diamond') b += `<path d="M${x} ${r1(y - z)} L${r1(x + z)} ${y} L${x} ${r1(y + z)} L${r1(x - z)} ${y} Z"${f}/>`;
        if (kind === 'cross') b += n === k ? `<circle cx="${x}" cy="${y}" r="${z}"${f}/>` : `<line x1="${r1(x - z)}" y1="${y}" x2="${r1(x + z)}" y2="${y}"/><line x1="${x}" y1="${r1(y - z)}" x2="${x}" y2="${r1(y + z)}"/>`;
        if (kind === 'ring') b += `<circle cx="${x}" cy="${y}" r="${z}"${f}/><circle cx="${x}" cy="${y}" r="1.6"/>`;
        if (kind === 'bar') b += n === k ? `<rect x="${r1(x - z)}" y="${r1(y - 1.6)}" width="${2 * z}" height="3.2" rx="1.6"${f}/>` : `<line x1="${r1(x - z)}" y1="${y}" x2="${r1(x + z)}" y2="${y}"/>`;
        if (kind === 'half') b += `<path d="M${r1(x - z)} ${r1(y + 2)} A${z} ${z} 0 0 1 ${r1(x + z)} ${r1(y + 2)} Z"${f}/>`;
      }
    return svg(b);
  },
  // six sessions: the outline changes every time, the core never moves
  constant() {
    const z = 4.5, gap = 4, n = 6;
    const x0 = (100 - (n * 2 * z + (n - 1) * gap)) / 2 + z;
    let b = '';
    for (let i = 0; i < n; i++) {
      const x = r1(x0 + i * (2 * z + gap)), y = 40;
      const outline = [
        `<circle cx="${x}" cy="${y}" r="${z}"/>`,
        `<rect x="${r1(x - z)}" y="${r1(y - z)}" width="${2 * z}" height="${2 * z}" rx="3"/>`,
        `<rect x="${r1(x - z)}" y="${r1(y - z)}" width="${2 * z}" height="${2 * z}" rx="0.8"/>`,
        `<path d="M${x} ${r1(y - z - 1)} L${r1(x + z + 1)} ${y} L${x} ${r1(y + z + 1)} L${r1(x - z - 1)} ${y} Z"/>`,
        `<path d="M${x} ${r1(y - z - 1.5)} L${r1(x + z + 1)} ${r1(y + z - 0.5)} L${r1(x - z - 1)} ${r1(y + z - 0.5)} Z"/>`,
        `<circle cx="${x}" cy="${y}" r="${z}"/>`,
      ][i];
      b += outline + `<circle cx="${x}" cy="${y}" r="1.3" ${FILL}/>`;
    }
    return svg(b);
  },
  // three stages, each larger than the last; the last outgrows the frame
  grow() {
    return svg(`<circle cx="20" cy="40" r="3" ${FILL}/><circle cx="40" cy="40" r="8"/><circle cx="84" cy="40" r="30"/>`, true);
  },
  // something at rest on a line
  pebble() {
    return svg(`<line x1="${M}" y1="54" x2="${100 - M}" y2="54"/><ellipse cx="50" cy="45" rx="13" ry="9" vector-effect="non-scaling-stroke"/>`);
  },
};

const NAMES = Object.keys(MOTIFS);

// Each published piece has its own motif, chosen for what it's about: an
// abstract hint, not an illustration, and never one used twice. A piece not
// listed here (a new one) falls back to a seeded pick.
const ASSIGNED: Record<string, [string, Params?]> = {
  'numbers travel. arguments don’t.': ['departs'],
  'decide and move on': ['fork'],
  'changing the format': ['shapes'],
  'this too shall pass': ['horizon'],
  'thinking for myself': ['flag'],
  'the future of design, revisited': ['spiral'],
  'the answer is always yes': ['steps'],
  'this workman blames his tools': ['ruler', { ceiling: true }],
  'taste is triage': ['choices'],
  friction: ['rotor'],
  'building genie changed me': ['grow'],
  'when ux becomes ax': ['fan'],
  'designing your own relevance': ['line'],
  'the ramstack': ['layers'],
  'the future of design': ['venn', { n: 2 }],
  'design tokens: a shared language that outlasts the tools': ['punch'],
  'the distance between request and outcome is now your brand': ['measure'],
  'designing genie: pioneering agentic ai for delivery': ['orbit', { centred: false }],
  'the best qa is the one that doesn’t have to happen': ['nested', { corner: false }],
  'manual vs. automated is the wrong debate': ['parallel'],
  'the guardrail problem': ['rails'],
  ego: ['mirror'],
  'make it pop': ['burst'],
  'imposter syndrome never fully goes away': ['dashed'],
  'design learnings & principles': ['arcs'],
  'design guard': ['prose'],
  'smart quotes fixer': ['letter', { ch: '”' }],
  mindset: ['untangle'],
  'still yourself': ['constant'],
};

/**
 * The motif drawing for a card. `seed` picks the motif and its details (use a
 * stable string such as title + route); `title` supplies the letter motif.
 */
export function generateMotif(seed: string, title: string): string {
  const h = hash(seed || 'motif');
  const assigned = ASSIGNED[(title || '').trim().toLowerCase()];
  if (assigned) return MOTIFS[assigned[0]](makeRng(h), title, assigned[1] || {});
  return MOTIFS[NAMES[h % NAMES.length]](makeRng(h), title || seed, {});
}
